import { useEffect, useMemo, useState } from 'react'
import { FileText, MoonStar, Pin, PinOff } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { adminMutate, usePermissions, useToast } from '../../../admin/primitives.js'
import { Field, TextInput } from '../../../admin/form.js'
import { useConfirm } from '../../../shell/confirm.js'
import { Link } from '../../../router.js'
import { remplacerRecherche } from '../../../finance/adresse-ecran.js'
import { fmtDateHeure, lireSupervision, messageEchecMutation, type Lecture } from '../../api.js'
import { BandeauFige, BandeauLecture, LIBELLE_GRAVITE, Pastille, TON_GRAVITE, fmtNombre, nomSalle, ordreSalle, renommerSalles, useVueSonde } from '../commun.js'
import {
  LIBELLE_DETAIL, LIBELLE_ORIGINE, LIBELLE_PREVOL, LIBELLE_RAPPORT, LIBELLE_TYPE, LIBELLE_VERIF, TON_PREVOL, TON_RAPPORT, TON_VERIF,
  borner, dateNuit, duree, estDetailNuit, estListeAnnotations, estRapport, estStatut, estZones, fenetreRejeu, heureParis, libelleSource,
  tonNuit, totalTrous,
  type DetailNuit, type ModeFenetre, type RapportMatin,
} from './nuits-model.js'
import { RejeuNuit } from './Rejeu.js'
import { AnnotationsNuit } from './Annotations.js'
import './nuits.css'
import { useMenuSonde } from '../menu-sonde.js'

/**
 * TECHNIQUE › INFRASTRUCTURE › NUITS › UNE NUIT (`/infrastructure/nuits/:date`).
 *
 * Le rapport de la nuit et son REJEU, lus dans Sonde par le relais de Gestion :
 * `GET /v1/nights/:date` (résumé, pré-vols, anomalies, incidents, rapport du
 * matin), `/replay?at&zone` (l'état du club à l'instant choisi), `/report?
 * preview=1` (aperçu du rapport pas encore préparé), `/v1/annotations?night=`.
 *
 * Gestes : épingler (garder le détail au-delà de la rétention) et désépingler
 * (baisse de rétention : step-up, et une confirmation qui dit la conséquence),
 * droit sonde.settings.manage ; noter un fait, droit sonde.annotate.
 *
 * Heures : toutes en heure de Paris, calculées depuis l'instant UTC ; la nuit
 * du 24 au 25/10 (25 heures) dit « (été) » ou « (hiver) » entre 2 h et 3 h.
 * L'instant rejoué se garde dans l'adresse (`?at=`) : on peut le partager.
 */

const DATE_NUIT = /^\d{4}-\d{2}-\d{2}$/

function Tuile({ lib, val, sous, ton }: { lib: string; val: string; sous?: string | null; ton?: 'warn' | 'err' | null }) {
  return (
    <div className={`sv-nuit-tuile${ton ? ` sv-nuit-tuile--${ton}` : ''}`}>
      <span className="sv-nuit-tuile__lib">{lib}</span>
      <span className="sv-nuit-tuile__val">{val}</span>
      {sous ? <span className="sv-nuit-tuile__sous">{sous}</span> : null}
    </div>
  )
}

function Rapport({ r, envoye }: { r: RapportMatin; envoye: string | null }) {
  return (
    <div className="sv-nuit-rapport">
      <p className="sv-nuit-rapport__sujet">{r.subject}</p>
      <div className="sv-nuit-puces">
        <Pastille ton={TON_RAPPORT[r.status] ?? 'gris'}>{LIBELLE_RAPPORT[r.status] ?? r.status}</Pastille>
        <span className="lc-hint">préparé le {fmtDateHeure(r.generated_at)}{envoye ? ` · envoyé le ${fmtDateHeure(envoye)}` : ''}</span>
      </div>
      {r.sections.map((s) => (
        <section key={s.code}>
          <h3><Pastille ton={TON_RAPPORT[s.status] ?? 'gris'}>{LIBELLE_RAPPORT[s.status] ?? s.status}</Pastille> {s.title}</h3>
          {s.lines.length > 0 ? <ul>{s.lines.map((l, k) => <li key={k}>{l}</li>)}</ul> : null}
        </section>
      ))}
      {r.witness_fr ? <p className="lc-hint">{r.witness_fr}</p> : null}
      {r.shadow_mode ? <p className="lc-hint">Préparé en mode ombre : Sonde observait sans alerter pour les sources non confiées.</p> : null}
    </div>
  )
}

export function NuitPage({ date }: { date: string }) {
  const menu = useMenuSonde()
  const meta = useShellMeta()
  const perms = usePermissions()
  const peut = (x: string) => !perms.loading && perms.permissions.includes(x)
  const toast = useToast()
  const confirm = useConfirm()
  const valide = DATE_NUIT.test(date)

  const detail = useVueSonde(valide ? `nights/${date}` : null, estDetailNuit, 60_000)
  const d: DetailNuit | null = detail.lecture?.etat === 'ok' ? detail.lecture.data : detail.precedente
  const statut = useVueSonde('status', estStatut, 300_000)
  const zones = useVueSonde('zones', estZones, 600_000)
  const notes = useVueSonde(valide ? `annotations?night=${date}&limit=200` : null, estListeAnnotations, 60_000)
  const listeNotes = (notes.lecture?.etat === 'ok' ? notes.lecture.data : notes.precedente)?.items ?? []

  const st = statut.lecture?.etat === 'ok' ? statut.lecture.data : statut.precedente
  const libellesSources = useMemo(() => new Map((st?.sources ?? []).map((s) => [s.code, s.label])), [st])
  const zv = zones.lecture?.etat === 'ok' ? zones.lecture.data : zones.precedente
  // ROOM 1, ROOM 2, ROOM 3 d'abord, sous leur seul nom (jamais les anciens libellés de Sonde).
  const libellesZones = useMemo(() => new Map([...(zv?.zones ?? [])]
    .sort((a, b) => ordreSalle(a.code) - ordreSalle(b.code))
    .map((z) => [z.code, nomSalle(z.code, z.label)])), [zv])

  // « Maintenant » avance : la fin de la fenêtre d'une nuit en cours suit l'heure.
  const [maintenant, setMaintenant] = useState(() => Date.now())
  useEffect(() => { const t = setInterval(() => setMaintenant(Date.now()), 30_000); return () => clearInterval(t) }, [])

  const [mode, setMode] = useState<ModeFenetre>('soiree')
  const [curseur, setCurseur] = useState<number | null>(null)
  const fenetre = d ? fenetreRejeu(d.night, mode, maintenant) : null
  const curseurEffectif = fenetre && curseur !== null ? borner(curseur, fenetre) : null

  // Instant de départ : celui de l'adresse, sinon le premier incident de la soirée, sinon l'ouverture.
  useEffect(() => {
    if (!d || curseur !== null) return
    const now = Date.now()
    const fJ = fenetreRejeu(d.night, 'journee', now)
    if (!fJ) return
    const fS = fenetreRejeu(d.night, 'soiree', now)
    const demande = Date.parse(new URLSearchParams(window.location.search).get('at') ?? '')
    if (Number.isFinite(demande) && demande >= fJ.debut && demande <= fJ.fin) {
      if (!fS || fS.mode !== 'soiree' || demande < fS.debut || demande > fS.fin) setMode('journee')
      setCurseur(borner(demande, fJ))
      return
    }
    const f = fS ?? fJ
    const premier = d.incidents.map((i) => Date.parse(i.opened_at)).filter((t) => t >= f.debut && t <= f.fin).sort((a, b) => a - b)[0]
    const ouv = d.night.opening_at ? Date.parse(d.night.opening_at) : NaN
    setCurseur(borner(premier ?? (f.enCours ? f.fin : Number.isFinite(ouv) && ouv >= f.debut && ouv <= f.fin ? ouv : f.debut), f))
  }, [d, curseur])

  // L'instant rejoué vit dans l'adresse (remplacée, jamais empilée).
  useEffect(() => {
    if (curseurEffectif === null) return
    const t = setTimeout(() => remplacerRecherche(`/infrastructure/nuits/${date}`, new URLSearchParams({ at: new Date(curseurEffectif).toISOString() })), 400)
    return () => clearTimeout(t)
  }, [curseurEffectif, date])

  /** Aller à un instant de la nuit (incident, trou, note) : élargit la fenêtre s'il le faut. */
  const aller = (ms: number) => {
    if (!d) return
    const now = Date.now()
    const fJ = fenetreRejeu(d.night, 'journee', now)
    if (!fJ) return
    const fS = fenetreRejeu(d.night, 'soiree', now)
    if (mode === 'soiree' && (!fS || fS.mode !== 'soiree' || ms < fS.debut || ms > fS.fin)) setMode('journee')
    setCurseur(borner(ms, fJ))
    document.getElementById('sv-nuit-rejeu')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  /* ─── Épingler / désépingler ─── */
  const [formEpingle, setFormEpingle] = useState(false)
  const [motif, setMotif] = useState('')
  const [occupe, setOccupe] = useState(false)

  const epingler = async () => {
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message_fr?: string }>('POST', `/api/v1/supervision/v/nights/${date}/pin`,
      motif.trim() ? { reason: motif.trim().slice(0, 500) } : {}, { headers: { 'Idempotency-Key': crypto.randomUUID() } })
    setOccupe(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', r.data.message_fr ?? 'Nuit épinglée.')
      setFormEpingle(false); setMotif(''); detail.recharger()
    } else toast('error', messageEchecMutation(r.status, r.data, 'la nuit n’a pas été épinglée'))
  }

  const desepingler = async () => {
    const ok = await confirm({
      title: `Désépingler la nuit du ${dateNuit(date)} ?`,
      message: 'Son détail suivra la rétention normale et son archive sera supprimée : c’est parfois la seule copie de ses événements. Votre identité sera redemandée.',
      ...(d?.night.pinned ? { details: [{ label: 'Épinglée par', value: `${d.night.pinned.by}, le ${fmtDateHeure(d.night.pinned.at)}` }, ...(d.night.pinned.reason ? [{ label: 'Motif', value: d.night.pinned.reason }] : [])] } : {}),
      variant: 'sensitive',
      confirmLabel: 'Désépingler',
    })
    if (!ok) return
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message_fr?: string }>('POST', `/api/v1/supervision/v/nights/${date}/unpin`, {},
      { headers: { 'Idempotency-Key': crypto.randomUUID() } })
    setOccupe(false)
    if (r.status >= 200 && r.status < 300) { toast('ok', r.data.message_fr ?? 'Nuit désépinglée.'); detail.recharger() }
    else toast('error', messageEchecMutation(r.status, r.data, 'la nuit reste épinglée'))
  }

  /* ─── Aperçu du rapport du matin ─── */
  const [apercu, setApercu] = useState<Lecture<RapportMatin> | null>(null)
  const [apercuEnCours, setApercuEnCours] = useState(false)
  const voirApercu = async () => {
    setApercuEnCours(true)
    setApercu(await lireSupervision(`/v/nights/${date}/report?preview=1`, estRapport))
    setApercuEnCours(false)
  }

  if (!valide) {
    return (
      <Shell active="infrastructure" meta={meta}>
        <main className="lc-page lc-stagger sv-nuit">
          <Link className="lc-crumb" href="/infrastructure/nuits">← Nuits</Link>
          <h1 className="lc-title">Nuit introuvable</h1>
          <p className="lc-subtitle">Adresse invalide : une nuit s’ouvre par sa date (AAAA-MM-JJ).</p>
        </main>
      </Shell>
    )
  }

  const n = d?.night ?? null
  const c = n?.counts ?? null
  const t = n ? tonNuit(n) : null
  const trous = n ? totalTrous(n.sources_unavailable) : null
  const collector = st?.collector ? { ok: st.collector.ok ?? null, stale: st.collector.stale ?? null, detail_fr: st.collector.detail ?? null } : null

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-stagger lc-page--large sv-nuit">
        <Link className="lc-crumb" href="/infrastructure/nuits">← Nuits</Link>
        <header className="sv-nuit-tete">
          <div>
            <h1 className="lc-title"><MoonStar size={24} aria-hidden="true" /> Nuit du {dateNuit(date, true)}</h1>
            {n ? (
              <div className="sv-nuit-puces">
                {n.kind ? <span className="sv-nuit-neutre">{LIBELLE_TYPE[n.kind] ?? n.kind}</span> : null}
                <Pastille ton="gris">{n.status === 'open' ? 'En cours' : 'Close'}</Pastille>
                {t ? <Pastille ton={t.ton} title={t.source === 'rapport' ? 'Verdict du rapport du matin' : 'D’après les comptes de Sonde'}>{t.libelle}</Pastille> : null}
                {n.pinned ? <span className="sv-nuit-neutre" title={n.pinned.reason ?? undefined}><Pin size={13} aria-hidden="true" /> Épinglée par {n.pinned.by}</span> : null}
                {d ? <span className="sv-nuit-neutre">{LIBELLE_DETAIL[d.data.detail] ?? d.data.detail}</span> : null}
              </div>
            ) : null}
            {n ? (
              <p className="lc-subtitle">
                {n.label ? `${n.label} · ` : ''}
                {n.opening_at || n.closing_at ? `Ouverture ${heureParis(n.opening_at)} → ${heureParis(n.closing_at)}` : 'Pas d’horaires d’ouverture'}
                {n.origin ? ` (${LIBELLE_ORIGINE[n.origin] ?? n.origin})` : ''}
              </p>
            ) : null}
          </div>
          {n && peut('sonde.settings.manage') ? (
            <div className="sv-nuit-tete__actions">
              {n.pinned ? (
                <button type="button" className="lc-btn" onClick={() => void desepingler()} disabled={occupe}><PinOff size={14} aria-hidden="true" /> Désépingler</button>
              ) : !formEpingle ? (
                <button type="button" className="lc-btn" onClick={() => setFormEpingle(true)}><Pin size={14} aria-hidden="true" /> Épingler</button>
              ) : null}
            </div>
          ) : null}
        </header>

        {formEpingle && n && !n.pinned ? (
          <section className="lc-panel" aria-label="Épingler la nuit">
            <div className="lc-panel__body">
              <div className="sv-nuit-epingle">
                <p className="sv-conseq">Épingler garde le détail complet de cette nuit (mesures brutes, événements) au-delà de la rétention normale.</p>
                <Field label="Motif (facultatif)" helper="Ex. : nuit de la coupure secteur, à garder pour l’assurance.">
                  <TextInput value={motif} onChange={setMotif} maxLength={500} ariaLabel="Motif de l’épinglage" />
                </Field>
                <div className="sv-nuit-epingle__pied">
                  <button type="button" className="lc-btn lc-btn--primary" onClick={() => void epingler()} disabled={occupe}><Pin size={14} aria-hidden="true" /> {occupe ? 'Épinglage…' : 'Épingler la nuit'}</button>
                  <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { setFormEpingle(false); setMotif('') }}>Annuler</button>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <div className="sv-nuit-section">
          <BandeauLecture lecture={detail.lecture} depuis={n?.computed_at ?? null} />
          {n?.status === 'open' ? <BandeauFige collector={collector} /> : null}
        </div>
        {!d && !detail.lecture ? <p className="sv-nuit-vide">Lecture de Sonde…</p> : null}

        {n ? (
          <>
            <div className="sv-nuit-tuiles" aria-label="Chiffres de la nuit">
              <Tuile lib="Incidents" val={fmtNombre(c?.incidents)} ton={c && c.incidents_critical > 0 ? 'err' : c && c.incidents > 0 ? 'warn' : null}
                     sous={c ? `${c.incidents_critical} critique${c.incidents_critical > 1 ? 's' : ''}` : null} />
              <Tuile lib="Alertes" val={fmtNombre(c?.alerts)} sous={c ? `dont ${c.alerts_shadow} en ombre (non envoyées)` : null} />
              <Tuile lib="Passages hors ligne" val={fmtNombre(c?.offline_transitions)} sous={c ? `${c.devices_offline} appareil${c.devices_offline > 1 ? 's' : ''} concerné${c.devices_offline > 1 ? 's' : ''}` : null} />
              <Tuile lib="Anomalies" val={fmtNombre(c?.anomalies)} sous="par rapport aux soirées comparables" />
              <Tuile lib="Trous de supervision" val={fmtNombre(trous?.nombre)} sous={trous && trous.nombre > 0 ? `${duree(trous.secondes)} sans voir certains appareils` : 'aucune source muette ≥ 1 min'} />
            </div>
            <p className="lc-hint">Source : Sonde{n.computed_at ? ` · résumé calculé le ${fmtDateHeure(n.computed_at)}` : ' · résumé pas encore calculé'}{c === null ? ' · comptes indisponibles : « — »' : ''}</p>

            <RejeuNuit nuit={n} fenetre={fenetre} mode={mode} onMode={setMode} curseur={curseurEffectif} onCurseur={setCurseur}
                       incidents={d!.incidents} annotations={listeNotes} libellesZones={libellesZones} libellesSources={libellesSources}
                       detailTechnique={peut('sonde.details.read')} />

            <AnnotationsNuit lecture={notes.lecture} liste={listeNotes} recharger={notes.recharger} curseur={curseurEffectif}
                             nuitOuverte={n.status === 'open'} peutAnnoter={peut('sonde.annotate')} libellesZones={libellesZones} onAller={aller} />

            <div className="sv-nuit-deux">
              <section className="lc-panel" aria-labelledby="sv-nuit-rap-t">
                <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-nuit-rap-t">Rapport du matin</h2></header>
                <div className="lc-panel__body">
                  {d!.report ? <Rapport r={d!.report} envoye={n.report?.sent_at ?? null} /> : (
                    <>
                      <p className="sv-nuit-vide">Pas encore préparé : Sonde le prépare chaque matin à l’heure réglée.</p>
                      {apercu?.etat === 'ok' ? <div className="sv-nuit-plus"><p className="lc-hint">Aperçu calculé à la demande, pas envoyé :</p><Rapport r={apercu.data} envoye={null} /></div> : null}
                      {apercu && apercu.etat !== 'ok' ? <p className="sv-bandeau sv-bandeau--gris sv-nuit-plus" role="status">{apercu.message}</p> : null}
                      {apercu?.etat !== 'ok' ? (
                        <button type="button" className="lc-btn sv-nuit-plus" onClick={() => void voirApercu()} disabled={apercuEnCours}>
                          <FileText size={14} aria-hidden="true" /> {apercuEnCours ? 'Calcul…' : 'Voir un aperçu'}
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </section>

              <section className="lc-panel" aria-labelledby="sv-nuit-incn-t">
                <header className="lc-panel__head">
                  <h2 className="lc-section-title" id="sv-nuit-incn-t">Incidents de la nuit</h2>
                  <span className="lc-panel__spacer" />
                  <span className="lc-hint">{d!.incidents.length}{d!.incidents.length >= 100 ? ' (100 premiers)' : ''}</span>
                </header>
                <div className="lc-panel__body">
                  {d!.incidents.length === 0 ? <p className="sv-nuit-vide">Aucun incident ouvert cette nuit.</p> : (
                    <ul className="sv-nuit-lignes">
                      {d!.incidents.map((i) => (
                        <li key={i.id} className="sv-nuit-l">
                          <span className="sv-nuit-l__t"><button type="button" onClick={() => aller(Date.parse(i.opened_at))} title="Rejouer cet instant">{heureParis(i.opened_at)}</button></span>
                          <div className="sv-nuit-l__corps">
                            <span className="sv-nuit-l__titre">
                              <Pastille ton={TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille>
                              <Link href={`/infrastructure/incidents/${encodeURIComponent(i.id)}`} {...menu.incident({ id: i.id, titre: renommerSalles(i.title), status: i.status })}>{renommerSalles(i.title)}</Link>
                            </span>
                            <span className="sv-nuit-l__meta">
                              {i.resolved_at ? `Résolu à ${heureParis(i.resolved_at)}` : 'Pas encore résolu'}
                              {i.affected_count > 0 ? ` · ${i.affected_count} appareil${i.affected_count > 1 ? 's' : ''} touché${i.affected_count > 1 ? 's' : ''}` : ''}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>

            <div className="sv-nuit-deux">
              <section className="lc-panel" aria-labelledby="sv-nuit-trous-t">
                <header className="lc-panel__head">
                  <h2 className="lc-section-title" id="sv-nuit-trous-t">Trous de supervision</h2>
                  <span className="lc-panel__spacer" />
                  <span className="lc-hint">sources muettes ≥ 1 min</span>
                </header>
                <div className="lc-panel__body">
                  {n.sources_unavailable.length === 0 ? <p className="sv-nuit-vide">Aucun : toutes les sources ont répondu, sans silence d’une minute ou plus.</p> : (
                    <>
                      <p className="sv-nuit-vide">Pendant ces trous, les appareils de la source n’ont pas été vus : ils sont gris dans le rejeu, ni en ligne ni en panne.</p>
                      <ul className="sv-nuit-lignes sv-nuit-plus">
                        {[...n.sources_unavailable].sort((a, b) => a.from.localeCompare(b.from)).map((x) => (
                          <li key={`${x.source}-${x.from}`} className="sv-nuit-l">
                            <span className="sv-nuit-l__t"><button type="button" onClick={() => aller(Date.parse(x.from))} title="Rejouer cet instant">{heureParis(x.from)}</button></span>
                            <div className="sv-nuit-l__corps">
                              <span className="sv-nuit-l__titre"><Pastille ton="gris">Muette</Pastille> {libelleSource(x.source, libellesSources)}</span>
                              <span className="sv-nuit-l__meta">{x.to ? `jusqu’à ${heureParis(x.to)}` : 'toujours muette'} · {duree(x.seconds)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {n.rooms.length > 0 ? (
                    <>
                      <h3 className="lc-section-title sv-nuit-section">Salles cette nuit</h3>
                      <ul className="sv-nuit-lignes sv-nuit-plus">
                        {n.rooms.map((s) => (
                          <li key={s.zone} className="sv-nuit-l">
                            <span className="sv-nuit-l__t">{s.on_at ? heureParis(s.on_at) : '—'}</span>
                            <div className="sv-nuit-l__corps">
                              <span className="sv-nuit-l__titre">
                                {libellesZones.get(s.zone) ?? s.zone}
                                {s.cut_at ? <Pastille ton="err">Coupure à {heureParis(s.cut_at)}</Pastille> : null}
                              </span>
                              <span className="sv-nuit-l__meta">
                                {s.on_at ? `Allumée à ${heureParis(s.on_at)}` : 'Pas allumée pendant la nuit'}
                                {s.off_at ? ` · éteinte à ${heureParis(s.off_at)}` : ''}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              </section>

              <section className="lc-panel" aria-labelledby="sv-nuit-pv-t">
                <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-nuit-pv-t">Pré-vols et anomalies</h2></header>
                <div className="lc-panel__body">
                  {d!.preflight.length === 0 ? <p className="sv-nuit-vide">Aucun pré-vol pour cette nuit (Sonde en fait avant chaque soirée, par défaut à H−60 et H−30).</p> : (
                    <ul className="sv-nuit-lignes">
                      {[...d!.preflight].sort((a, b) => b.lead_min - a.lead_min).map((pv) => (
                        <li key={`${pv.lead_min}-${pv.computed_at}`} className="sv-nuit-l">
                          <span className="sv-nuit-l__t">H−{pv.lead_min}</span>
                          <div className="sv-nuit-l__corps">
                            <span className="sv-nuit-l__titre"><Pastille ton={TON_PREVOL[pv.status] ?? 'gris'}>{LIBELLE_PREVOL[pv.status] ?? pv.status}</Pastille> {pv.summary_fr}</span>
                            <span className="sv-nuit-l__meta">calculé à {heureParis(pv.computed_at)} pour une ouverture à {heureParis(pv.opening_at)}</span>
                            {pv.checks.length > 0 ? (
                              <details className="sv-nuit-details">
                                <summary>{pv.checks.length} vérification{pv.checks.length > 1 ? 's' : ''}</summary>
                                <ul className="sv-nuit-lignes">
                                  {pv.checks.map((ck) => (
                                    <li key={ck.code} className="sv-nuit-l">
                                      <span className="sv-nuit-l__t"><Pastille ton={TON_VERIF[ck.status] ?? 'gris'}>{LIBELLE_VERIF[ck.status] ?? ck.status}</Pastille></span>
                                      <div className="sv-nuit-l__corps">
                                        <span className="sv-nuit-l__titre">{ck.label}</span>
                                        <span className="sv-nuit-l__meta">{ck.message_fr}</span>
                                        {ck.items.filter((it) => it.status !== 'ok').slice(0, 12).map((it, k) => (
                                          <span key={`${it.uid ?? it.name}-${k}`} className="sv-nuit-l__meta">· {it.name} : {it.detail_fr}</span>
                                        ))}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <h3 className="lc-section-title sv-nuit-section">Anomalies</h3>
                  {d!.anomaly_state ? <p className="sv-nuit-vide sv-nuit-plus">{d!.anomaly_state.message_fr}</p> : null}
                  {d!.anomalies.length === 0 ? (!d!.anomaly_state ? <p className="sv-nuit-vide sv-nuit-plus">Aucune anomalie relevée par rapport aux soirées comparables.</p> : null) : (
                    <ul className="sv-nuit-lignes sv-nuit-plus">
                      {d!.anomalies.map((a) => (
                        <li key={a.id} className="sv-nuit-l">
                          <span className="sv-nuit-l__t"><button type="button" onClick={() => aller(Date.parse(a.first_seen_at))} title="Rejouer cet instant">{heureParis(a.first_seen_at)}</button></span>
                          <div className="sv-nuit-l__corps">
                            <span className="sv-nuit-l__titre">
                              <Pastille ton={TON_GRAVITE[a.severity] ?? 'gris'}>{LIBELLE_GRAVITE[a.severity] ?? a.severity}</Pastille>
                              {a.uid ? <Link href={`/infrastructure/appareils/${encodeURIComponent(a.uid)}`}>{a.title}</Link> : <span>{a.title}</span>}
                            </span>
                            <span className="sv-nuit-l__meta">{a.occurrences} fois · jusqu’à {heureParis(a.last_seen_at)}{a.resolved_at ? ` · résolue à ${heureParis(a.resolved_at)}` : ''}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>
          </>
        ) : null}
      </main>
    </Shell>
  )
}
